import { useCallback } from "react";
import { supabase } from "../../../supabaseClient";
import { notify } from "../../UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../../utility/errorSanitization";
import { applyTicketsAfterTeamCreate } from "../../../utility/brackets/api";

export const useCreateTeamActions = (
  setTeams,
  setCurrentStep,
  setErrors,
  setIsSubmitting,
  resetForm,
  validateStep,
  onTeamCreated = null
) => {
  const handleNext = useCallback(
    (
      currentStep,
      selectedSquad,
      selectedEvent,
      teamName,
      bowlers,
      sidePotEvents,
      setErrors
    ) => {
      const errors = validateStep(
        currentStep,
        selectedSquad,
        selectedEvent,
        teamName,
        bowlers
      );

      if (Object.keys(errors).length === 0) {
        // If we're on step 2 and there are no side pot events, skip to submission
        if (currentStep === 2 && sidePotEvents.length === 0) {
          setCurrentStep(4); // Skip to submission
        } else {
          setCurrentStep(currentStep + 1);
        }
      } else {
        setErrors(errors);
      }
    },
    [validateStep, setCurrentStep]
  );

  const handlePrevious = useCallback(
    (currentStep, setCurrentStep) => {
      setCurrentStep(currentStep - 1);
    },
    [setCurrentStep]
  );

  const handleSubmit = useCallback(
    async (
      e,
      selectedEvent,
      teamName,
      bowlers,
      currentOrganization,
      user,
      squadID,
      tournamentID,
      setTeams,
      setIsSubmitting,
      resetForm,
      shouldClose = true,
      successMessage = null
    ) => {
      e.preventDefault();

      setIsSubmitting(true);

      try {
        // Prepare bowlers data
        const bowlersData = bowlers.map((bowler) => ({
          name: bowler.name,
          average: bowler.average
            ? Math.round(parseFloat(bowler.average))
            : null,
          hasAverage: bowler.hasAverage !== false, // Default to true for backward compatibility
          phone: bowler.phone || "",
          email: bowler.email || "",
          usbcID: bowler.usbcID || "",
          existingBowler: bowler.existingBowler,
          id: bowler.existingBowler ? bowler.id : null,
          eventEntries: [
            { eventsID: selectedEvent.id, entryMethod: "manual" },
            // Add selected side pot events for this specific bowler
            ...Object.entries(bowler.selectedSidePots || {})
              .filter(([_, isSelected]) => isSelected)
              .map(([eventId]) => ({
                eventsID: eventId,
                entryMethod: "manual",
              })),
            ...(bowler.eventEntries || []),
          ],
        }));

        // Validate required parameters
        if (
          !squadID ||
          squadID === "" ||
          squadID === null ||
          squadID === undefined
        ) {
          throw new Error(`Squad ID is required. Received: ${squadID}`);
        }
        if (
          !tournamentID ||
          tournamentID === "" ||
          tournamentID === null ||
          tournamentID === undefined
        ) {
          throw new Error(
            `Tournament ID is required. Received: ${tournamentID}`
          );
        }

        // Call RPC
        const rpcData = {
          team_name: selectedEvent.bowlerQuantity > 2 ? teamName : null,
          squad_id: squadID,
          tournament_id: tournamentID,
          organization_id: currentOrganization.id,
          user_id: user.id,
          bowlers_data: bowlersData,
          events_id: selectedEvent.id,
        };

        const { data, error } = await supabase.rpc(
          "create_team_with_event",
          rpcData
        );

        if (error) {
          throw error;
        }

        try {
          await applyTicketsAfterTeamCreate({
            teamId: data?.id,
            squadId: squadID,
            formBowlers: bowlers,
          });
        } catch (ticketError) {
          console.error("Bracket tickets after team create:", ticketError);
          notify.error(
            "Team created. Bracket tickets could not be saved. Add them on Brackets."
          );
        }

        // Success - always update teams list, but only close form when shouldClose is true
        await Promise.resolve(setTeams(data, shouldClose));
        resetForm();

        notify.success(
          successMessage ||
            (shouldClose
              ? "Team created successfully!"
              : "Team created! Ready to create another team.")
        );
        if (!shouldClose && onTeamCreated) {
          onTeamCreated();
        }
      } catch (error) {
        // Log the full error for debugging
        console.error("Team creation error:", error);
        console.error("Error details:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        const sanitizedError = sanitizeOperationError.create(
          error,
          "team",
          false
        );
        notify.error(sanitizedError);
        return {
          ok: false,
          error: {
            message: error.message || null,
            details: error.details || null,
            hint: error.hint || null,
            code: error.code || null,
          },
        };
      } finally {
        setIsSubmitting(false);
      }
      return { ok: true };
    },
    [setTeams, resetForm]
  );

  return {
    handleNext,
    handlePrevious,
    handleSubmit,
  };
};
